import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db, auth } from "./firebase";
import {
  sendSignInLinkToEmail,
  onAuthStateChanged
} from "firebase/auth";
import { collection, getDocs, doc, setDoc, serverTimestamp, query, where, getDoc, addDoc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import BookingFlow from "./BookingFlow"; // Import the BookingFlow component
import "./therapist.css";

// --- ASSET PATHS ---
const ASSETS = {
  hero: "/Nabi UI Assets/Assets_Images/Image_Therapist hero section.png",
  videoPlaceholder: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23E5E7EB'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/></svg>",
  playIcon: "/Nabi UI Assets/Assets_Icon/Icon_Play circle.svg",
  icons: {
    starFilled: "/Nabi UI Assets/Assets_Icon/Icon_Star filled.svg",
    starUnfilled: "/Nabi UI Assets/Assets_Icon/Icon_Star unfilled.svg",
    heartFilled: "/Nabi UI Assets/Assets_Icon/Icon_Favourites filled.svg",
    heartEmpty: "/Nabi UI Assets/Assets_Icon/Icon_Favourite unfilled.svg",
    checkFilled: "/Nabi UI Assets/Assets_Icon/Icon_Check circle filled.svg",
    radioChecked: "/Nabi UI Assets/Assets_Icon/Icon_Radio button_Checked.svg",
    radioUnchecked: "/Nabi UI Assets/Assets_Icon/Icon_Radio button_Unchecked.svg",
    close: "/Nabi UI Assets/Assets_Icon/Icon_Close.svg",
  }
};


// --- STUDENT VERIFICATION MODAL ---
const StudentVerificationModal = ({ isOpen, onClose, onVerify }) => {
  const [idNumber, setIdNumber] = useState("");
  const [idFile, setIdFile] = useState(null);
  const [graduationDate, setGraduationDate] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const storage = getStorage();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!idNumber || !idFile || !graduationDate) {
      alert("Please provide ID number, ID card image, and expected graduation date.");
      return;
    }

    setLoading(true);
    try {
      // 1. Upload ID Image
      const storageRef = ref(storage, `student_verifications/${Date.now()}_${idFile.name}`);
      await uploadBytes(storageRef, idFile);
      const downloadUrl = await getDownloadURL(storageRef);

      // 2. Mock verification for now (just pass the data back)
      onVerify({
        idNumber,
        idCardUrl: downloadUrl,
        graduationDate,
        timestamp: new Date()
      });

      alert("Verification data submitted to admin successfully! Your student status will be verified within 12 hours.");
    } catch (error) {
      console.error("Verification error:", error);
      alert("Failed to submit verification details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="therapist-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <motion.div
            className="therapist-modal-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            style={{ backgroundColor: 'white', padding: '30px', borderRadius: '24px', width: '100%', maxWidth: '400px', position: 'relative', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}
          >
            <button onClick={onClose} style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#999' }}>✕</button>

            <h2 className="therapist-modal-heading" style={{ fontSize: '1.5rem', fontFamily: "'Syne', sans-serif", color: '#003049', marginBottom: '10px' }}>Student Verification</h2>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>Upload your valid student ID card, enter your ID number, and provide your expected graduation date to unlock special student rates.</p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#003049' }}>Student ID Number</label>
                <input
                  type="text"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder="Enter your ID number"
                  style={{ padding: '14px', borderRadius: '12px', border: '1px solid #E2E8F0', fontSize: '14px', outline: 'none' }}
                  required
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#003049' }}>Expected Graduation Date</label>
                <input
                  type="date"
                  value={graduationDate}
                  onChange={(e) => setGraduationDate(e.target.value)}
                  style={{ padding: '14px', borderRadius: '12px', border: '1px solid #E2E8F0', fontSize: '14px', outline: 'none', fontFamily: "'Syne', sans-serif" }}
                  required
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#003049' }}>Upload ID Card Image</label>
                <div
                  onClick={() => document.getElementById('id-upload').click()}
                  style={{
                    border: '2px dashed #E2E8F0',
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#F8FAFC',
                    overflow: 'hidden',
                    height: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {previewUrl ? (
                    <img src={previewUrl} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="ID Preview" />
                  ) : (
                    <div style={{ color: '#64748B' }}>
                      <div style={{ fontSize: '24px', marginBottom: '5px' }}>📷</div>
                      <div style={{ fontSize: '12px' }}>Tap to upload ID card photo</div>
                    </div>
                  )}
                </div>
                <input id="id-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '16px',
                  backgroundColor: '#003049',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(0, 48, 73, 0.2)',
                  marginTop: '10px'
                }}
              >
                {loading ? "Verifying..." : "Submit for Verification"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const getInitials = (name) => {
  if (!name) return "TP";
  const parts = name.split(" ");
  if (parts.length > 1) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export default function TherapistPage({ user }) {
  console.log(user, "tyjfhgkbjnm,");
  const [view, setView] = useState("list");
  const [selectedDr, setSelectedDr] = useState(null);

  // Video Player State
  const [isPlaying, setIsPlaying] = useState(false);

  // Verification States
  const [isVerified, setIsVerified] = useState(false);
  const [studentVerificationStatus, setStudentVerificationStatus] = useState(null);

  // Package Selection State
  const [packageType, setPackageType] = useState("student");
  const [selectedSessionCount, setSelectedSessionCount] = useState(1); // 1, 2, or 4

  // Emails
  const [stuEmail, setStuEmail] = useState("");
  const [showStudentVerificationModal, setShowStudentVerificationModal] = useState(false);
  const [showStudentSuccessPopup, setShowStudentSuccessPopup] = useState(false);
  const [studentId, setStudentId] = useState("");
  const [studentIdImage, setStudentIdImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [dbTherapists, setDbTherapists] = useState([]);
  const [authEmail, setAuthEmail] = useState("");

  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchTherapists = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "therapists"));
        const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setDbTherapists(fetched);
      } catch (err) {
        console.error("Failed to fetch therapists:", err);
      }
    };
    fetchTherapists();
  }, []);

  useEffect(() => {
    const checkUserVerification = async () => {
      // Check both auth.currentUser and the passed user prop
      const uid = user?.uid || auth.currentUser?.uid;
      const email = user?.email || auth.currentUser?.email;
      if (uid || email) {
        try {
          let verifiedFlag = false;
          let statusFlag = null;

          if (uid) {
            const q = query(collection(db, "users"), where("uid", "==", uid));
            const userSnap = await getDocs(q);
            if (!userSnap.empty) {
              const userData = userSnap.docs[0].data();
              verifiedFlag = userData.isStudentVerified === true;
              statusFlag = userData.studentVerificationStatus || null;
            }
          }
          if (!statusFlag && email) {
            const qEmail = query(collection(db, "users"), where("email", "==", email));
            const snapEmail = await getDocs(qEmail);
            if (!snapEmail.empty) {
              const userData = snapEmail.docs[0].data();
              verifiedFlag = userData.isStudentVerified === true;
              statusFlag = userData.studentVerificationStatus || null;
            }
          }

          // Check student_verifications collection if status is still null
          if (!statusFlag) {
            let verifDocs = [];
            if (uid) {
              const qVerif = query(collection(db, "student_verifications"), where("userId", "==", uid));
              const verifSnap = await getDocs(qVerif);
              verifDocs.push(...verifSnap.docs.map(d => d.data()));
            }
            if (email) {
              const qVerifEmail = query(collection(db, "student_verifications"), where("userEmail", "==", email));
              const verifSnapEmail = await getDocs(qVerifEmail);
              verifDocs.push(...verifSnapEmail.docs.map(d => d.data()));
            }
            if (verifDocs.length > 0) {
              verifDocs.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
              statusFlag = verifDocs[0].status || null;
            }
          }

          setIsVerified(verifiedFlag);
          setStudentVerificationStatus(statusFlag);
        } catch (err) {
          console.error("Error checking verification:", err);
        }
      }
    };
    checkUserVerification();
  }, [user]);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!selectedDr) {
        setReviews([]);
        return;
      }

      // Clear previous reviews immediately to prevent showing old data
      setReviews([]);

      try {
        const tEmail = selectedDr.email?.toLowerCase().trim();
        const tId = selectedDr.id;

        if (!tEmail && !tId) return;

        let data = [];
        let snap;

        // 1. Primary query by email if available
        if (tEmail) {
          const qEmail = query(
            collection(db, "session_reviews"),
            where("therapistEmail", "==", tEmail),
            where("isAccepted", "==", true)
          );
          snap = await getDocs(qEmail);
          data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }

        // 2. Fallback/Complementary query by ID if email query was empty or not possible
        if (data.length === 0 && tId) {
          const qId = query(
            collection(db, "session_reviews"),
            where("therapistId", "==", tId),
            where("isAccepted", "==", true)
          );
          snap = await getDocs(qId);
          data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }

        // Sort by newest
        data.sort((a, b) => (b.timestamp?.toMillis() || 0) - (a.timestamp?.toMillis() || 0));
        setReviews(data);
      } catch (err) {
        console.error("Error fetching reviews:", err);
      }
    };

    const checkUserVerification = async () => {
      if (auth.currentUser) {
        const uid = auth.currentUser.uid;
        const email = auth.currentUser.email;
        let verifiedFlag = false;
        let statusFlag = null;

        if (uid) {
          const q = query(collection(db, "users"), where("uid", "==", uid));
          const userSnap = await getDocs(q);
          if (!userSnap.empty) {
            const userData = userSnap.docs[0].data();
            verifiedFlag = userData.isStudentVerified === true;
            statusFlag = userData.studentVerificationStatus || null;
          }
        }
        if (!statusFlag && email) {
          const qEmail = query(collection(db, "users"), where("email", "==", email));
          const snapEmail = await getDocs(qEmail);
          if (!snapEmail.empty) {
            const userData = snapEmail.docs[0].data();
            verifiedFlag = userData.isStudentVerified === true;
            statusFlag = userData.studentVerificationStatus || null;
          }
        }

        if (!statusFlag) {
          let verifDocs = [];
          if (uid) {
            const qVerif = query(collection(db, "student_verifications"), where("userId", "==", uid));
            const verifSnap = await getDocs(qVerif);
            verifDocs.push(...verifSnap.docs.map(d => d.data()));
          }
          if (email) {
            const qVerifEmail = query(collection(db, "student_verifications"), where("userEmail", "==", email));
            const verifSnapEmail = await getDocs(qVerifEmail);
            verifDocs.push(...verifSnapEmail.docs.map(d => d.data()));
          }
          if (verifDocs.length > 0) {
            verifDocs.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
            statusFlag = verifDocs[0].status || null;
          }
        }

        setIsVerified(verifiedFlag);
        setStudentVerificationStatus(statusFlag);
      } else {
        setIsVerified(false);
        setStudentVerificationStatus(null);
      }
    };

    if (view === "profile" && selectedDr) {
      fetchReviews();

      // Also re-fetch the latest therapist data to show dashboard updates immediately
      const refreshDrData = async () => {
        try {
          const drRef = doc(db, "therapists", selectedDr.id);
          const drSnap = await getDoc(drRef);
          if (drSnap.exists()) {
            setSelectedDr({ ...drSnap.data(), id: drSnap.id });
          }
        } catch (err) {
          console.error("Error refreshing therapist data:", err);
        }
      };
      refreshDrData();
    } else {
      setReviews([]); // Reset if modal closes
    }
  }, [selectedDr?.id, view]); // Use selectedDr.id to avoid infinite loops

  // Form Data
  const [bookingData, setBookingData] = useState({
    fullName: "", age: "", gender: "", contact: "",
    trustName: "", trustRelationship: "", trustContact: "",
    date: "", timeSlot: "", message: "",
    billingFirst: "", billingLast: "", billingAddress: ""
  });

  // Calculate Price logic
  const getPrice = (type, count) => {
    if (type === 'student') {
      if (count === 1) return 750;
      // if (count === 1) return 2 ;
    } else {
      if (count === 1) return 1200;
      if (count === 2) return 2300;
      if (count === 4) return 4600;
      return 1200; // Fallback
    }
  };

  const getPackageName = (type, count) => {
    if (type === 'student') return "Student Package (1 Session)";
    if (count === 1) return "Non-Student Package (1 Session)";
    if (count === 2) return "Non-Student Package (2 Sessions)";
    if (count === 4) return "Non-Student Package (4 Sessions)";
    return "Therapy Package";
  };

  const toggleFavorite = (e, id) => {
    e.stopPropagation();
    setFavorites(prev => prev.includes(id) ? prev.filter(favId => favId !== id) : [...prev, id]);
  };

  const currentUserEmail = (user?.email || authEmail || "").toLowerCase().trim();
  const allowedEmails = ["janureddy3112@gmail.com", "charithadoddaka51@gmail.com","dheerajvanga9@gmail.com","soornahemanth143@gmail.com","sandhyachinnamsetti@gmail.com"]; 
  const allTherapists = dbTherapists.filter(dr => {
    const name = dr.name?.toLowerCase().trim() || "";
    const isTarget = name.includes("jahnavi") && name.includes("pragmatix");
    if (isTarget) {
      return allowedEmails.includes(currentUserEmail);
    }
    return true
  });

  const filteredTherapists = showFavsOnly
    ? allTherapists.filter(dr => favorites.includes(dr.id))
    : allTherapists;

  const renderStars = (rating) => {
    const num = Number(rating) || 5;
    return (
      <span style={{ fontSize: '18px', marginRight: '4px' }}>
        {num >= 4.5 ? '🤩' : num >= 3.5 ? '😊' : num >= 2.5 ? '😐' : num >= 1.5 ? '😕' : '😞'}
      </span>
    );
  };

  // Check Auth State
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setAuthEmail(authUser.email || "");
        try {
          const uid = authUser.uid;
          const email = authUser.email;
          let verifiedFlag = false;
          let statusFlag = null;

          if (uid) {
            const q = query(collection(db, "users"), where("uid", "==", uid));
            const userSnap = await getDocs(q);
            if (!userSnap.empty) {
              const userData = userSnap.docs[0].data();
              verifiedFlag = userData.isStudentVerified === true;
              statusFlag = userData.studentVerificationStatus || null;
            }
          }
          if (!statusFlag && email) {
            const qEmail = query(collection(db, "users"), where("email", "==", email));
            const snapEmail = await getDocs(qEmail);
            if (!snapEmail.empty) {
              const userData = snapEmail.docs[0].data();
              verifiedFlag = userData.isStudentVerified === true;
              statusFlag = userData.studentVerificationStatus || null;
            }
          }

          // Check student_verifications collection if status is still null
          if (!statusFlag) {
            let verifDocs = [];
            if (uid) {
              const qVerif = query(collection(db, "student_verifications"), where("userId", "==", uid));
              const verifSnap = await getDocs(qVerif);
              verifDocs.push(...verifSnap.docs.map(d => d.data()));
            }
            if (email) {
              const qVerifEmail = query(collection(db, "student_verifications"), where("userEmail", "==", email));
              const verifSnapEmail = await getDocs(qVerifEmail);
              verifDocs.push(...verifSnapEmail.docs.map(d => d.data()));
            }
            if (verifDocs.length > 0) {
              verifDocs.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
              statusFlag = verifDocs[0].status || null;
            }
          }

          setIsVerified(verifiedFlag);
          setStudentVerificationStatus(statusFlag);
        } catch (err) {
          console.error("Error checking auth verification:", err);
        }
      } else {
        setAuthEmail("");
        setIsVerified(false);
        setStudentVerificationStatus(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Student Verification Handler
  const handleVerifyEmail = async (emailToVerify) => {
    if (!emailToVerify) return alert("Enter email address.");

    const actionCodeSettings = {
      url: window.location.href,
      handleCodeInApp: true
    };

    try {
      setLoading(true);
      await sendSignInLinkToEmail(auth, emailToVerify, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', emailToVerify);
      alert(`Verification link sent to ${emailToVerify}. Please check your inbox.`);
    } catch (error) {
      alert("Error sending link: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveBookingStep = async (nextView) => {
    if (packageType === 'student' && !auth.currentUser && !isVerified) {
      alert("Please verify your student email to proceed.");
      return;
    }

    try {
      setLoading(true);
      const userId = auth.currentUser?.email || `guest_${bookingData.contact || Date.now()}`;
      const docRef = doc(db, "bookings", userId);
      await setDoc(docRef, {
        ...bookingData,
        therapistId: selectedDr?.id,
        therapistName: selectedDr?.name,
        price: getPrice(packageType, selectedSessionCount),
        isStudent: packageType === 'student',
        packageType: packageType,
        sessionCount: selectedSessionCount,
        status: "draft",
        lastUpdated: serverTimestamp()
      }, { merge: true });
      setView(nextView);
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
  };

  // Custom Radio Button Component
  const CustomRadio = ({ checked }) => (
    <img
      src={checked ? ASSETS.icons.radioChecked : ASSETS.icons.radioUnchecked}
      alt="radio"
      style={{ width: '20px', height: '20px', marginRight: '10px' }}
    />
  );

  // Package Option Row Component
  const PackageOption = ({ type, count, label, duration, price, textColor = '#515151', priceColor = '#724b53' }) => {
    const isSelected = packageType === type && selectedSessionCount === count;
    return (
      <div
        style={styles.radioRow}
        onClick={() => { setPackageType(type); setSelectedSessionCount(count); }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CustomRadio checked={isSelected} />
          <div>
            <span style={{ ...styles.radioText, color: textColor }}>{label}</span>
            {duration && <div style={styles.subText}>{duration}</div>}
          </div>
        </div>
        <div style={{color: priceColor }}>₹
        
        <span style={{ ...styles.radioPrice, color: priceColor }}>{price}</span>
        </div>
      </div>
    );
  };

  // --- RENDER BOOKING FLOW ---
  if (view === "intake") {
    return (
      <BookingFlow
        therapist={selectedDr}
        packageType={getPackageName(packageType, selectedSessionCount)}
        sessionCount={selectedSessionCount}
        price={getPrice(packageType, selectedSessionCount)}
        onBack={() => setView("profile")} // Go back to Profile Modal
        user={user}
      />
    );
  }


  return (
    <div style={styles.container}>
      {view === "list" && (
        <section>
          <div className="therapist-hero-banner" style={styles.heroBanner}>
            <div style={styles.heroOverlay}>
              {/* <p className="therapist-hero-subtitle" style={styles.heroSubTitle}>Need support?</p> */}
              <h1 className="therapist-hero-title" style={styles.heroTitle}>Connect with our <br /> Psychologists</h1>
            </div>
          </div>

          <div style={styles.gridContainer}>
            <div className="therapist-list-header" style={styles.listHeader}>
              <h2 style={styles.sectionTitle}>Meet our Therapists ({filteredTherapists.length})</h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '43px' }}>
                <span style={{ fontSize: '0.9rem', color: '#888', }}><span style={{color: '#003049',}}> *</span> Avail the <span style={{color: '#003049',fontFamily:'Syne' }}>TRUSTFIRST</span> coupon for your first free individual therapy session during checkout.</span>
                <div style={styles.toggleContainer} onClick={() => setShowFavsOnly(!showFavsOnly)}>
                  <div style={{
                    width: '44px', height: '24px',
                    background: showFavsOnly ? '#F07167' : '#E5E7EB',
                    borderRadius: '24px', position: 'relative', cursor: 'pointer', marginRight: '10px',
                    transition: 'background 0.3s ease'
                  }}>
                    <div style={{
                      width: '18px', height: '18px', background: 'white', borderRadius: '50%',
                      position: 'absolute', top: '3px', left: showFavsOnly ? '23px' : '3px',
                      transition: 'left 0.3s'
                    }} />
                  </div>
                  <span style={{ fontSize: '0.9rem', color: '#555' }}>Show only favourites</span>
                </div>
              </div>
            </div>

            <div style={styles.grid}>
              {filteredTherapists.map(dr => (
                <div key={dr.id} style={styles.card}>
                  <div style={styles.cardTop}>
                    <button style={styles.favBtn} onClick={(e) => toggleFavorite(e, dr.id)}>
                      <img src={favorites.includes(dr.id) ? ASSETS.icons.heartFilled : ASSETS.icons.heartEmpty} alt="fav" style={{ width: '40px' }} />
                    </button>
                    {dr.image ? (
                      <img src={dr.image} alt={dr.name} style={styles.drImg} />
                    ) : (
                      <div style={{ ...styles.drImg, display: 'flex', alignItems: 'center', justifyContent: 'center', background: "linear-gradient(180deg, #FFE1C5 20.91%, #FF8A67 198.29%)", color: '#C57B57', fontSize: '48px', fontWeight: 'bold', fontFamily: "'Syne', sans-serif" }}>
                        {getInitials(dr.name)}
                      </div>
                    )}
                  </div>
                  <div style={styles.cardInfo}>
                    <h3 style={styles.drName}>{dr.name}</h3>
                    <p style={styles.drQual}>{dr.qual} | {dr.location}</p>
                    {/* <div style={styles.ratingRow}>
                      {renderStars(dr.rating)}
                      <span style={{ marginLeft: '8px', fontSize: '0.85rem', color: '#6B7280' }}>{dr.rating} {dr.reviews}</span>
                    </div> */}
                    <button onClick={() => { setSelectedDr(dr); setView("profile"); setIsPlaying(false); }} style={styles.outlineBtn}>Know More & Packages</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <AnimatePresence>
        {view === "profile" && (
          <motion.div className="therapist-modal-overlay" style={styles.modalOverlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <motion.div
              key={selectedDr?.id}
              className="therapist-modal-content"
              style={styles.modalContent}
              initial={{ y: 50 }}
              animate={{ y: 0 }}
            >

              <div style={styles.modalHeader}>
                <h2 className="therapist-modal-heading" style={styles.modalHeading}>Profile Overview</h2>
                <button onClick={() => { setView("list"); setIsPlaying(false); }} style={styles.closeBtn}>
                  <img src={ASSETS.icons.close} alt="Close" style={{ width: '24px' }} />
                </button>
              </div>

              {/* TOP SECTION: ABOUT + VIDEO */}
              <div style={styles.topSection}>
                <div className="therapist-about-container" style={styles.aboutContainer}>
                  <div style={styles.aboutTextContent}>
                    <h3 style={styles.sectionHeader}>About Me</h3>
                    <p style={styles.aboutText}>
                      {selectedDr?.bio || "The therapist provided a very safe and supportive space. Highly recommend for anyone looking for clinical help."}
                    </p>

                    {((selectedDr?.expertise || selectedDr?.specializations || selectedDr?.specialties || selectedDr?.tags) && (selectedDr?.expertise || selectedDr?.specializations || selectedDr?.specialties || selectedDr?.tags).length > 0) && (
                      <>
                        <h3 style={styles.sectionHeader}>Expertise In</h3>
                        <div style={styles.tagCloud}>
                          {(selectedDr?.expertise || selectedDr?.specializations || selectedDr?.specialties || selectedDr?.tags).map((tag, i) => <span key={i} style={styles.tag}>{tag}</span>)}
                        </div>
                      </>
                    )}
                  </div>

                  {/* --- UPDATED VIDEO WRAPPER --- */}
                  <div className="therapist-video-wrapper" style={styles.videoWrapper}>
                    {selectedDr?.introVideo ? (
                      isPlaying ? (
                        <video
                          width="100%"
                          height="100%"
                          controls
                          autoPlay
                          src={selectedDr.introVideo}
                          style={{ borderRadius: "16px", objectFit: "cover" }}
                        />
                      ) : (
                        <>
                          {selectedDr.image ? (
                            <img src={selectedDr.image} style={styles.videoPlaceholder} alt="Video Thumbnail" />
                          ) : (
                            <div style={{ ...styles.videoPlaceholder, display: 'flex', alignItems: 'center', justifyContent: 'center', background: "linear-gradient(180deg, #FFE1C5 20.91%, #FF8A67 198.29%)", color: '#C57B57', fontSize: '80px', fontWeight: 'bold', fontFamily: "'Syne', sans-serif" }}>
                              {getInitials(selectedDr.name)}
                            </div>
                          )}
                          <div
                            style={styles.playOverlay}
                            onClick={() => setIsPlaying(true)}
                          >
                            <img
                              src={ASSETS.playIcon}
                              alt="Play"
                              style={{ width: "64px", height: "64px", cursor: "pointer" }}
                            />
                          </div>
                        </>
                      )
                    ) : (
                      selectedDr?.image ? (
                        <img src={selectedDr.image} style={styles.videoPlaceholder} alt="Therapist Profile" />
                      ) : (
                        <div style={{ ...styles.videoPlaceholder, display: 'flex', alignItems: 'center', justifyContent: 'center', background: "linear-gradient(180deg, #FFE1C5 20.91%, #FF8A67 198.29%)", color: '#C57B57', fontSize: '80px', fontWeight: 'bold', fontFamily: "'Syne', sans-serif" }}>
                          {getInitials(selectedDr?.name)}
                        </div>
                      )
                    )}
                  </div>
                  {/* --- END UPDATED VIDEO WRAPPER --- */}

                </div>
              </div>

              {/* BOTTOM SECTION: PACKAGES & REVIEWS */}
              <div className="therapist-bottom-section" style={styles.bottomSection}>

                <div style={styles.packagesColumn}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 0, marginBottom: '20px' }}>
                    <h3 style={{ ...styles.sectionHeader, margin: 0 }}>Important Information</h3>
                    <button
                      onClick={() => setShowInstructionsModal(true)}
                      title="Instructions"
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: '1px solid #003049',
                        backgroundColor: 'transparent',
                        color: '#003049',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        padding: 0
                      }}
                    >
                      i
                    </button>
                  </div>
                  <div className="therapist-packages-grid" style={styles.packagesGrid}>

                    {/* --- STUDENT CARD --- */}
                    <div
                      className="therapist-package-card"
                      style={{
                        ...styles.packageCard,
                        border: packageType === 'student' ? '2px solid #F07167' : '1px solid #FFD8AF'
                      }}
                      onClick={() => setPackageType('student')}
                    >
                      {/* TOP GRADIENT BANNER */}
                      <div style={{
                        background: 'linear-gradient(180deg, #FF985E 0%, #FFDAB2 100%)',
                        borderRadius: '16px',
                        padding: '12px',
                        height: '100px',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        marginBottom: '24px'
                      }}>
                        {isVerified ? (
                          <div style={{ ...styles.cardBadgeGreen, top: '16px', right: '16px' }}>
                            <img src={ASSETS.icons.checkFilled} alt="verified" style={{ width: '12px', marginRight: '5px' }} />
                            Verified
                          </div>
                        ) : studentVerificationStatus === 'rejected' ? (
                          <div style={{ ...styles.cardBadgeGreen, top: '16px', right: '16px', backgroundColor: '#003049', color: 'white' }}>
                            Verification Rejected
                          </div>
                        ) : studentVerificationStatus === 'pending' ? (
                          <div style={{ ...styles.cardBadgeGreen, top: '16px', right: '16px', backgroundColor: 'rgba(0, 0, 0, 0.7);', color: 'white' }}>
                             Verification Ongoing
                          </div>
                        ) : (
                          <div style={{ ...styles.cardBadgeBlack, top: '16px', right: '16px' }}>
                             Not Verified
                          </div>
                        )}
                        <h2 style={{ ...styles.packTitle, margin: 0, fontSize: '1.8rem', fontWeight: '500', color: '#724b53' }}>Student</h2>
                      </div>

                      <div style={{ ...styles.radioGroup, margin: '0 10px 30px' }}>
                        <PackageOption type="student" count={1} label="One session" price={getPrice('student', 1)} />
                      </div>

                      {studentVerificationStatus === 'rejected' ? (
                        <div style={{ marginTop: 'auto' }}>
                          <p style={{ fontSize: '12px', color: '#742A2A', marginBottom: '10px', textAlign: 'center', lineHeight: '1.4', fontWeight: '500' }}>
                            Rejection based on graduation date. Please book a slot through Non-Student packages.
                          </p>
                          <button
                            disabled
                            style={{ ...styles.scheduleBtn, background: '#ccc', color: '#666', cursor: 'not-allowed' }}
                          >
                            Student Access Disabled
                          </button>
                        </div>
                      ) : studentVerificationStatus === 'pending' ? (
                        <div style={{ marginTop: 'auto' }}>
                          <p style={{ fontSize: '12px', color: '#000', marginBottom: '10px', textAlign: 'center', lineHeight: '1.4', fontWeight: '500' }}>
                            Verification ongoing: Your student details are currently being reviewed by admin.
                          </p>
                          <button
                            disabled
                            style={{ ...styles.scheduleBtn, background: '#ccc', color: '#666', cursor: 'not-allowed' }}
                          >
                            Student Verification Ongoing
                          </button>
                        </div>
                      ) : !isVerified && packageType === 'student' ? (
                        <div style={{ marginTop: 'auto' }}>
                          <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px', textAlign: 'center', lineHeight: '1.4' }}>
                            Verification required: Please provide your student ID card and details to access this student discounted Therapy.
                          </p>
                          <button
                            style={{ ...styles.scheduleBtn, background: '#003049', color: '#fff' }}
                            onClick={() => setShowStudentVerificationModal(true)}
                          >
                            Student Verification
                          </button>
                        </div>
                      ) : (
                        packageType === 'student' && isVerified && (
                          <button
                            onClick={() => setView("intake")}
                            style={{
                              ...styles.scheduleBtn,
                              background: '#24272F',
                              color: '#fff',
                              marginTop: 'auto'
                            }}
                          >
                            Schedule a Session
                          </button>
                        )
                      )}
                    </div>

                    {/* --- NON-STUDENT CARD --- */}
                    <div
                      className="therapist-package-card"
                      style={{
                        ...styles.packageCard,
                        border: packageType === 'non-student' ? '2px solid #F07167' : '1px solid #FFD8AF'
                      }}
                      onClick={() => setPackageType('non-student')}
                    >
                      {/* TOP GRADIENT BANNER */}
                      <div style={{
                        background: "linear-gradient(180deg, #FF5F4E -16.04%, #FFDAB2 88.68%)",
                        borderRadius: '16px',
                        padding: '10px',
                        height: '100px',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        marginBottom: '24px'
                      }}>
                        <h2 style={{ ...styles.packTitle, margin: 0, fontSize: '1.8rem', fontWeight: '500', color: '#C35146' }}>Non-Student</h2>
                      </div>

                      <div style={{ ...styles.radioGroup, margin: '0 10px 30px' }}>
                        <PackageOption type="non-student" count={1} label="One session" price={getPrice('non-student', 1)} textColor="#515151" priceColor="#c35146" />
                        <div style={styles.divider}></div>
                        <PackageOption type="non-student" count={2} label="Two therapy session" duration="Duration: 4 Weeks" price={getPrice('non-student', 2)} textColor="#515151" priceColor="#c35146" />
                        <div style={styles.divider}></div>
                        <PackageOption type="non-student" count={4} label="Four therapy session" duration="Duration: 8 Weeks" price={getPrice('non-student', 4)} textColor="#515151" priceColor="#c35146" />
                      

                      <button
                        onClick={() => setView("intake")}
                        style={{
                          ...styles.scheduleBtn,
                          background: packageType === 'non-student' ? '#24272F' : 'rgba(255,255,255,0.5)',
                          color: packageType === 'non-student' ? '#fff' : '#555',
                          marginTop: '2px'
                        }}
                      >
                        Schedule a Session
                      </button>
                  </div>
                    </div>
                  </div>
                </div>

                {/* REVIEWS SIDEBAR */}
                <div style={styles.reviewsColumn}>
                  <h3 style={{ ...styles.sectionHeader, marginTop: 0 }}>Latest Reviews</h3>
                  <div style={{ maxHeight: '450px', overflowY: 'auto', paddingRight: '10px' }}>
                    {reviews.length > 0 ? (
                      reviews.map((rev, idx) => (
                        <div key={rev.id || idx} style={styles.reviewCard}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
                            <span style={{ fontSize: '24px' }}>
                              {["😠", "🙁", "😐", "🙂", "🤩"][rev.rating - 1] || "🙂"}
                            </span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#F07167', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              {["Poor", "Fair", "Good", "Very Good", "Excellent"][rev.rating - 1] || "Rated"}
                            </span>
                          </div>
                          <p style={styles.reviewText}>
                            "{rev.feedback || "The therapist provided a very safe and supportive space. Highly recommend for anyone looking for clinical help."}"
                          </p>
                          
                          <div style={styles.divider}></div>
                          <div>
                          <div style={styles.reviewerInfo}>
                            <div style={styles.reviewerAvatar}>
                              {rev.userName ? rev.userName.charAt(0).toUpperCase() : "U"}
                            </div>

                            <div>
                              <h4 style={styles.reviewerName}>{rev.userName || "Anonymous User"}</h4>
                              {rev.userType && <p style={styles.reviewerType}>{rev.userType}</p>}
                            </div>
                          </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', background: '#F8F9FA', borderRadius: '16px', color: '#999' }}>
                        <div style={{ fontSize: '32px', marginBottom: '10px' }}>⭐</div>
                        <p style={{ fontSize: '13px', margin: 0 }}>No reviews yet for this therapist. Be the first to share your experience!</p>
                      </div>
                    )}
                  </div>
                </div>

              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {showStudentVerificationModal && (
        <StudentVerificationModal
          isOpen={showStudentVerificationModal}
          onClose={() => setShowStudentVerificationModal(false)}
          onVerify={async (data) => {
            try {
              // 1. Save to Firestore
              await addDoc(collection(db, "student_verifications"), {
                ...data,
                userEmail: auth.currentUser?.email || "Guest",
                userId: auth.currentUser?.uid || "Guest",
                status: "pending",
                createdAt: serverTimestamp()
              });

              // 2. The email notification is now handled by a Firebase Cloud Function (onStudentVerificationCreated)
              // which triggers automatically when the document is created in Firestore.

              // Also update the user's profile document to pending
              if (auth.currentUser) {
                const q = query(collection(db, "users"), where("uid", "==", auth.currentUser.uid));
                const userSnap = await getDocs(q);
                if (!userSnap.empty) {
                  const userDocId = userSnap.docs[0].id;
                  await updateDoc(doc(db, "users", userDocId), {
                    studentVerificationStatus: 'pending'
                  });
                }
              }

              setStudentVerificationStatus('pending');
              setShowStudentVerificationModal(false);
              setShowStudentSuccessPopup(true);
            } catch (err) {
              console.error("Error saving verification:", err);
              alert("Failed to save verification data.");
            }
          }}
        />
      )}
      <AnimatePresence>
        {showStudentSuccessPopup && (
          <div className="therapist-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <motion.div
              className="therapist-modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{ backgroundColor: 'white', padding: '40px', borderRadius: '24px', width: '100%', maxWidth: '420px', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.2)', position: 'relative' }}
            >
              <div style={{ width: '70px', height: '70px', borderRadius: '50%', backgroundColor: '#FFE5D9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h2 style={{ fontSize: '1.6rem', fontFamily: "'Syne', sans-serif", color: '#003049', marginBottom: '15px', marginTop: 0 }}>Submission Successful!</h2>
              <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.6', marginBottom: '30px' }}>
                Your student verification data has been successfully submitted to the admin. It will be verified within <strong>12 hours</strong>.
              </p>
              <button
                onClick={() => setShowStudentSuccessPopup(false)}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#003049',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0, 48, 73, 0.2)'
                }}
              >
                Got it, Thanks!
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* INSTRUCTIONS MODAL */}
      <AnimatePresence>
        {showInstructionsModal && (
          <div style={styles.chatOverlay}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{ ...styles.chatModal, height: 'auto', padding: '24px', maxWidth: '400px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, color: '#003049', fontSize: '18px' }}>Instructions</h3>
                <button onClick={() => setShowInstructionsModal(false)} style={styles.chatCloseBtn}>✕</button>
              </div>
              <div style={{ fontSize: '14px', color: '#444', lineHeight: '1.6' }}>

                <p>First therapy session of 40 mins is on us .</p>
                <p>To ensure your therapist has enough time to prepare and offer the best support possible, therapy sessions can only be booked at least 7 hours in advance. Please choose a slot that is 7 hours or more from the current time.</p>
                {/* General data passed can be rendered here */}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  chatOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  },
  chatModal: {
    backgroundColor: '#FFF',
    borderRadius: '24px',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  },
  chatCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#888'
  },

  container: { backgroundColor: "#FFF7F1", minHeight: "100vh", fontFamily: "'Syne', sans-serif" },
  heroBanner: { height: "800px", backgroundImage: `url('${ASSETS.hero}')`, backgroundSize: "cover", backgroundPosition: "center", display: 'flex', alignItems: 'center', justifyContent: 'center' },
  heroOverlay: { textAlign: 'center', marginBottom: '250px' },
  heroSubTitle: { color: 'white', fontSize: '1.2rem', marginBottom: '10px', fontWeight: "500", },
  heroTitle: { color: '#fffff', fontSize: '3rem' },
  gridContainer: { fontFamily: "'Syne', sans-serif", fontWeight: "light", maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' },
  listHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', fontWeight: "light" },
  sectionTitle: { fontSize: '2rem', fontWeight: '500' },
  toggleContainer: { display: 'flex', alignItems: 'center', cursor: 'pointer' },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "30px" },
  card: { backgroundColor: "white", borderRadius: "20px", overflow: "hidden", boxShadow: "0 5px 15px rgba(0,0,0,0.1)" },
  cardTop: { background: "linear-gradient(180deg, #FFE1C5 20.91%, #FF8A67 198.29%)", height: '200px', position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  favBtn: { position: 'absolute', top: '15px', right: '15px', background: 'white', borderRadius: '50%', width: '36px', height: '36px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  drImg: { width: "100%", height: "100%", objectFit: "contain" },
  cardInfo: { padding: '20px' },
  drName: { fontSize: '1.2rem', fontWeight: '500', margin: '0 0 5px' },
  drQual: { color: '#6B7280', fontSize: '0.85rem' },
  ratingRow: { margin: '10px 0', display: 'flex', alignItems: 'center' },
  outlineBtn: { width: '100%', padding: '12px', border: '1px solid #D1D5DB', borderRadius: '8px', background: 'white', fontWeight: '300', cursor: 'pointer' },

  // MODAL STYLES
  modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "100px" },
  modalContent: { backgroundColor: "white", width: "95%", maxWidth: "1200px", height: 'auto', maxHeight: '75vh', overflowY: 'auto', padding: "40px", borderRadius: '24px', position: 'absolute',top:'70px' },
  cardBadgeBlack: { position: 'absolute', top: '20px', right: '20px', background: '#000000b3', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', zIndex: 5, fontWeight: '500' },
  cardBadgeGreen: { position: 'absolute', top: '20px', right: '20px', background: 'rgba(0, 0, 0, 0.7)', color: '#fff', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontFamily: 'Syne', display: 'flex', alignItems: 'center', zIndex: 5 },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', fontWeight: "normal", borderBottom: '1px solid #E5E7EB', paddingBottom: '20px' },
  modalHeading: { fontSize: '1.8rem', fontWeight: 'normal', margin: 0, color: '#000' },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 },

  // PROFILE LAYOUT
  topSection: { marginBottom: '40px' },
  aboutContainer: { display: 'flex', gap: 0, border: '1px solid #E5E7EB', borderRadius: '20px', overflow: 'hidden' },
  aboutTextContent: { flex: 1.2, fontFamily: "'Syne', sans-serif", fontWeight: "40", backgroundColor: '#FFF7F1', padding: '30px' },
  videoWrapper: { flex: 1, borderRadius: '0', overflow: 'hidden', height: 'auto', minHeight: '300px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000' },
  videoPlaceholder: { width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 },
  // playOverlay: { position: 'absolute', zIndex: 10 },
  playOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    cursor: "pointer"
  },
  sectionHeader: { fontSize: '1.4rem', fontWeight: '500', marginBottom: '16px', marginTop: '24px', color: '#111', fontFamily: "'Syne', sans-serif" },
  aboutText: { fontSize: '1rem', lineHeight: '1.6', color: '#222', marginBottom: '24px', fontFamily: "'Syne', sans-serif", fontWeight: '400' },
  tagCloud: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' },
  tag: { padding: '8px 18px', background: 'white', borderRadius: '25px', border: '1px solid #CBD5E1', fontSize: '0.9rem', color: '#111', fontWeight: '500', fontFamily: "'Syne', sans-serif" },

  // PACKAGES & REVIEWS LAYOUT
  bottomSection: { display: 'flex', gap: '40px', alignItems: 'flex-start' },
  packagesColumn: { flex: 2 },
  reviewsColumn: { flex: 1 },
  packagesGrid: { display: 'flex', gap: '25px' },

  // PACKAGE CARDS
  packageCard: { flex: 1, borderRadius: '24px', padding: '10px', background: "linear-gradient(180deg, rgba(226, 170, 130, 0.114) 0%, rgba(239, 179, 126, 0.099) 100%)", border: '1px solid #E5E7EB', position: 'relative', display: 'flex', flexDirection: 'column', minHeight: '450px', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },

  packTitle: { fontSize: '1.8rem', color: '#222', marginBottom: '30px', fontWeight: 'normal' },

  radioGroup: { display: 'flex', flexDirection: 'column', gap: '0', marginBottom: '30px' },
  radioRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', cursor: 'pointer' },
  radioText: { fontSize: '1rem', color: '#222', fontWeight: 'normal' },
  radioPrice: { fontWeight: '650', fontSize: '0.95rem', color: '#222' },
  subText: { fontSize: '0.75rem', color: '#666', marginTop: '4px' },
  divider: { height: '1px', backgroundColor: 'rgba(0,0,0,0.08)', width: '100%' },

  verifyInput: { width: '90%', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.5)', fontSize: '0.9rem', outline: 'none', backgroundColor: 'rgba(255,255,255,0.7)', marginBottom: '10px', fontFamily: "'Syne', sans-serif'" },
  verifyBtn: { width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', cursor: 'pointer', color: '#f17167', fontWeight: '40' },
  scheduleBtn: { width: '100%', padding: '14px', borderRadius: '12px', border: 'none', fontSize: '1rem', cursor: 'pointer', fontWeight: '40', transition: 'all 0.3s' },

  // REVIEWS
  reviewCard: { border: '1px solid #BBBDC3', padding: '20px', borderRadius: '16px', marginBottom: '20px', background: 'white' },
  reviewText: { fontSize: '0.9rem', color: '#555', fontStyle: 'italic', marginBottom: '15px', lineHeight: '1.5' },
  reviewerInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
  reviewerAvatar: { width: '40px', height: '40px', borderRadius: '50%', background: '#FFD8BE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 'normal', color: '#555' },
  reviewerName: { fontSize: '0.95rem', fontWeight: 'normal', margin: 0, color: '#222' },
  reviewerType: { fontSize: '0.8rem', color: '#888', margin: 0 },

  // GENERIC FORM
  formPage: { padding: '40px' },
  whiteBox: { background: 'white', padding: '40px', borderRadius: '20px', maxWidth: '600px', margin: '40px auto' },
  fInput: { padding: '12px', borderRadius: '8px', border: '1px solid #ddd', width: '100%', marginBottom: '15px' },
  buttonRow: { display: 'flex', justifyContent: 'space-between' },
  submitBtn: { padding: '12px 30px', background: '#F07167', color: 'white', borderRadius: '8px', border: 'none', cursor: 'pointer' },
  backBtn: { padding: '12px 20px', background: '#eee', color: '#333', borderRadius: '8px', border: 'none', cursor: 'pointer' }
};